/// <reference path="../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    import BlockTypeNaturalKey = Application.Models.BlockTypeNaturalKey;

    export interface ITrendingElement {
        type: 'stream' | 'networkNode' | 'sonLink' | 'sonElements' | 'digicon';
        id?: number;
        blockId?: number;
        param: string;
        unit: Unit;
        elementHref?: string; // only for data ports, instead of id
    }

    type Unit = 'bps' | '%' | 'kbps' | 'pkts/s' | 'ms' | 'us' | 'none';

    interface IRouteParams extends ng.route.IRouteParamsService {
        streamId?: string;  // only for streams

        clientId?: string;  // only for sonServices

        class?: string;     // only for sonLinks
        id?: string;        // only for sonLinks
        param?: string;     // only for sonLinks and data ports
        title?: string;     // only for digicons
        sourceId?: string;    // only for data ports
        sinkId?: string;    // only for data ports

        blockId?: string;
    }

    interface IDataSerie {
        data: number[][];
        label?: string;
        color: string;
        lines?: {
            lineWidth?: number;
            fill?: boolean;
            fillColor?: string;
        };
        points?: { show: boolean; };
    }

    interface IPlotSeries {
        minDataToPlot?: IDataSerie;
        avgDataToPlot?: IDataSerie;
        maxDataToPlot?: IDataSerie;
    }

    interface IPlotDigiconSeries {
        dataToPlotTrunk?: IDataSerie;
        dataToPlotFiles?: IDataSerie;
        dataToPlotAperi?: IDataSerie;
        dataToPlot360?: IDataSerie;
        dataToPlotEN07?: IDataSerie;
        dataToPlotEN08?: IDataSerie;

    }


    interface ITimeWindow {
        start: number;
        end: number;
        size: string;
    }

    interface IStats {
        min?: number;
        avg?: number;
        max?: number;
        var?: number;
        varPerc?: number;
    }

    interface IQuickZoomLinks {
        minutes: number;
        label: string;
        tooltip: string;
    }

    interface IMarking {
        xaxis: {
            from: number ,
            to: number
        };
        color: string;
        data: Models.IAlarm[];
    }

    interface IParam {
        href: string;
        param: string;
        title: string;
    }

    export class HistoricalTrendingController {
        public static $name: string = "historicalTrendingController";
        public static $inject: string[] = [
            '$scope',
            '$routeParams',
            '$timeout',
            'streamStatusService',
            'streamPipeService',
            'networkNodeService',
            'timezoneService',
            '$route',
            '$location',
            'alarmsService',
            '$q',
            'messageTranslationsService',
            'sonAdapterService',
            'blockService',
            '$rootScope',
        ];

        private static MAX_TIME_RANGE_MSECS: number = 7 * 24 * 3600 * 1000; // 7d
        private static INITIAL_DATA_MSECONDS: number = 12 * 3600 * 1000;    // 12h
        private static ZOOM_MIN_RANGE: number = 5 * 60 * 1000;              // 5m
        private static ZOOM_MIN_STEP: number = 1000 * 15;                   // 15s
        private static ZOOM_PERCENTAGE: number = 0.1;
        private static AROUND_MARKING_MODIFIER: number = 0.01;

        public title: string;
        public quickZoomLinks: IQuickZoomLinks[] = [
            {minutes: 7 * 24 * 60, label: '7d', tooltip: '7 days'},
            {minutes: 4 * 24 * 60, label: '4d', tooltip: '4 days'},
            {minutes: 2 * 24 * 60, label: '2d', tooltip: '2 days'},
            {minutes: 24 * 60, label: '1d', tooltip: '1 day'},
            {minutes: 12 * 60, label: '12h', tooltip: '12 hours'},
            {minutes: 8 * 60, label: '8h', tooltip: '8 hours'},
            {minutes: 2 * 60, label: '2h', tooltip: '2 hours'},
            {minutes: 60, label: '1h', tooltip: 'hour'},
            {minutes: 30, label: '30m', tooltip: '30 minutes'},
            {minutes: 5, label: '5m', tooltip: '5 minutes'},
        ];
        public currentMinutes: number;
        public blocks: Models.Block[] = [];
        public blockNames: string[] = [];

        // element info
        private element: ITrendingElement;
        private bitratesLabel: string[] = [];
        private bitrates: string[] = ["360_bitrate", "files_bitrate", "aperi_bitrate", "trunk_bitrate", "EN07_bitrate", "EN08_bitrate"];
        private trendingLoaded: boolean = false;
        private trendingRefreshing: boolean = false;
        private events: Models.FlexEvent[] = undefined;

        // trending-related
        private initialDateValue: number;
        private plotOptions: jquery.flot.plotOptions;
        private dataToPlot: IPlotSeries = {};
        private dataToPlotDigicon: IPlotDigiconSeries = {};
        private stats: IStats;
        private statsDigicon: IStats[];
        private currentTimeWindow: ITimeWindow = {
            start: 0,
            end: 0,
            size: undefined,
        };
        private currentTimeWindowUtc: ITimeWindow = {
            start: 0,
            end: 0,
            size: undefined,
        };
        private plot: jquery.flot.plot;
        private tz: string;

        private params: IParam[];
        private selectedParam: IParam;

        private currentInterval: {start: number, end: number};

        // Generate 36 0 points to draw an empty graph
        private static generateZeroBitrate(seconds: number, endDate: number): Models.IBitRateTrending[] {
            const ms: number = seconds * 1000;
            const sample: number = ms / 36;
            let currentDate: number = endDate - ms;
            let trending: Models.IBitRateTrending[] = [];

            for (let i: number = 0; i < 36; i++) {
                trending.push({
                    bitrate: 0,
                    date: new Date(currentDate).toString(),
                    minValue: 0,
                    maxValue: 0,
                    synthetic: true,
                });
                currentDate += sample;
            }
            return trending;

        }

        constructor(private $scope: ng.IScope,
                    private $routeParams: IRouteParams,
                    private $timeout: ng.ITimeoutService,
                    private streamStatusService: Services.StreamStatusService,
                    private streamPipeService: Services.StreamPipeService,
                    private networkNodeService: Services.NetworkNodeService,
                    private timezoneService: Services.TimezoneService,
                    private $route: ng.route.IRouteService,
                    private $location: ng.ILocationService,
                    private alarmsService: Services.AlarmsService,
                    private $q: ng.IQService,
                    private messageTranslationsService: Services.MessageTranslationsService,
                    private sonAdapterService: Services.SonAdapterService,
                    private blockService: Services.BlockService,
                    private $rootScope: Services.IFlexSettings,
                    ) {
            if ($routeParams.sourceId === '_' && $routeParams.sinkId === '_') {
                $location.path('/multiviewer');
            }
            this.tz = this.timezoneService.getEffectiveTimezone();
            this.initialDateValue = new Date().valueOf();
            this.setPlotOptions();
            this.loadTrendingElement().then(() => {
                this.setYAxisLabel();
                this.bindPlotEvents();
            });
        }

        public zoomMinutes(minutes: number): void {
            if (this.currentMinutes === minutes)
                return;
            const newInitialDataValue: number = new Date().valueOf();
            this.currentInterval = {
                start: newInitialDataValue - (minutes * 60 * 1000),
                end: newInitialDataValue,
            };
            this.loadTrendingData(this.currentInterval).then(() => {
                this.currentMinutes = minutes;
                this.initialDateValue = newInitialDataValue;
                const xMin: number = this.initialDateValue -  HistoricalTrendingController.MAX_TIME_RANGE_MSECS;
                let xMax: number;
                if (this.element.type === 'digicon') {
                    xMax = this.dataToPlotDigicon.dataToPlotTrunk.data[this.dataToPlotDigicon.dataToPlotTrunk.data.length - 1][0];
                }
                else {
                    xMax = this.dataToPlot.avgDataToPlot.data[this.dataToPlot.avgDataToPlot.data.length - 1][0];
                }
                this.plotOptions.xaxis.panRange = [xMin, xMax];
            });
            this.adjustXAxisAndTickSize();


        }

        public wheelEvent(event: { originalEvent?: { deltaY: number; }; }): void {
            this.currentMinutes = undefined;

            (event as ng.IAngularEvent).preventDefault();

            if (event.originalEvent.deltaY < 0) {
                this.zoom('in');
            } else {
                this.zoom('out');
            }
        }

        public changeBlock(newValue: string): void {
            let selectedBlock: Models.Block = this.blocks.filter((block: Models.IBlock) => {
                return block.name === newValue;
            })[0];

            if (selectedBlock) {
                this.events = undefined;
                this.selectBlockToShow(selectedBlock);
            }
        }

        public selectBlockToShow(block: Models.IBlock): void {
            this.title = block.name;
            this.element.blockId = block.id;
            this.loadTrendingData();
        }

        public changeParam(newParam: IParam): void {
            if (this.element.type === 'sonElements') {
                this.title = `Data Port ${newParam.title}`;
            }
            this.element.param = newParam.param;
            this.element.elementHref = newParam.href;
            this.loadTrendingData(this.currentInterval);
        }

        public exportToCsv(): ng.IPromise<void> {
            let secs: number = this.calculateSecs(this.currentInterval.start, this.currentInterval.end);
            if (this.element.type === 'stream') {
                return this.streamStatusService
                    .downloadStreamPipeTrendingData(this.element.id, this.currentInterval.end, secs);
            } else if (this.element.type === 'networkNode' && this.element.blockId) {
                return this.streamStatusService
                    .downloadBlockTrendingData(this.element.blockId, this.currentInterval.end, secs);
            } else if (this.element.type === 'sonLink') {
                return this.streamStatusService
                    .downloadParamTrendingData(`sonLinks/${this.element.id}`, this.element.param, this.currentInterval.end, secs);
            } else if (this.element.type === 'sonElements') {
                return this.streamStatusService
                    .downloadParamTrendingData(this.element.elementHref, this.element.param, this.currentInterval.end, secs);
            }
            else {
                this.$q.resolve();
            }
        }

        private calculateSecs(start: number, end: number): number {
            let secs: number = Math.floor((end - start) / 1000);
            if (secs < 60)
                secs = 60;
            return secs;
        }

        private bindPlotEvents(): void {
            const trendingGraph: JQuery.IJQueryFlotBind = $("#historical-stream");

            trendingGraph.bind("plothover", (event: JQueryEventObject, pos: jquery.flot.pos, item: jquery.flot.item) => {
                this.plotHover(event, pos, item);
            });
            trendingGraph.bind("plotclick", (event: JQueryEventObject, pos: jquery.flot.pos, item: jquery.flot.item) => {
                this.plotClick(event, pos, item);
            });
            trendingGraph.bind("plotpan", (event: JQueryEventObject, plot: jquery.flot.plot) => {
                this.plotPan('start', event, plot);
            });
            trendingGraph.bind("mouseup", (event: JQueryEventObject, plot: jquery.flot.plot) => {
                this.plotPan('end', event, plot);
            });
            this.$scope.$on('$destroy', () => {
                if (trendingGraph.length) {
                    trendingGraph.unbind('plothover');
                    trendingGraph.unbind('plotclick');
                    trendingGraph.unbind('plotpan');
                    trendingGraph.unbind('mouseup');
                }
                if (this.plot !== undefined) {
                    this.plot.destroy();
                    this.plot = undefined;
                }
            });
        }

        private plotHover(event: JQueryEventObject, pos: jquery.flot.pos, item: jquery.flot.item): void {
            if (this.isAroundMarking(pos)) {
                $("#historical-stream").css('cursor', 'pointer');
            } else {
                $("#historical-stream").css('cursor', 'default');
            }
            const timeFormat: string = 'DD-MM-YY HH:mm:ss';
            if (item) {
                let pointtDate: number = Math.floor(item.datapoint[0]);
                $("#tooltip").hide();
                $("#tooltip")
                    .css({
                        top: item.pageY - 35,
                        left: item.pageX - 100,
                    })
                    .html(`${item.datapoint[1].toFixed(2)} ${this.plotOptions.yaxes[0].axisLabel}
                           @ ${Common.Utils.epochMilliToFormat(pointtDate, timeFormat, this.tz)}`)
                    .fadeIn(200);
            } else {
                let htmlStr: string = this.generateTooltipForMarkings(pos, timeFormat);
                $("#tooltip").hide();
                if (!!htmlStr) {
                    $("#tooltip")
                        .css({
                            top: pos.pageY + 25,
                            left: pos.pageX - 20,
                        })
                        .html(htmlStr)
                        .fadeIn(200);
                }
            }
        }

        private generateTooltipForMarkings(pos: jquery.flot.pos, timeFormat: string): string {
            let htmlStr: string;
            let count: number = 0;
            for (let marking of this.plot.getOptions().grid.markings) {
                if (this.isMarkingBelow(pos, marking.xaxis.from)) {
                    count++;
                    if (count <= 15) {
                        if (!!htmlStr) {
                            htmlStr += '<br>';
                        } else {
                            htmlStr = '';
                        }
                        const nameDisplay: string = marking.data[0].nameDisplay ? marking.data[0].nameDisplay : marking.data[0].name;
                        htmlStr += `${marking.data[0].deviceName}: ${nameDisplay} is ${marking.data[0].lastValue}
                                        @ ${Common.Utils.epochMilliToFormat(marking.xaxis.from, timeFormat, this.tz)}`;
                    } else if (count === 16) {
                        htmlStr += '<br>...';
                    }
                }
            }
            return (count > 1) ? `${count} alarms:<br>${htmlStr}` : htmlStr;
        }

        private plotClick(event: JQueryEventObject, pos: jquery.flot.pos, item: jquery.flot.item): void {
            const markingTimestamp: number = this.getTimestampOfFirstMarkingBelow(pos);
            if (markingTimestamp) {
                this.currentMinutes = undefined;
                this.zoom('in', this.applyOffset(markingTimestamp, true));
            }
        }

        private plotPan(pan: 'start' | 'end', event: JQueryEventObject, plot: jquery.flot.plot): void {
            if (pan === 'start') {
                this.trendingRefreshing = true;
                this.currentMinutes = undefined;
            } else if (pan === 'end' && this.trendingRefreshing) {
                this.loadTrendingData({
                    start: this.applyOffset(Math.floor(this.plot.getAxes().xaxis.min as number), true),
                    end: this.applyOffset(Math.floor(this.plot.getAxes().xaxis.max as number), true),
                });
            }
        }

        private isAroundMarking(pos: jquery.flot.pos): boolean {
            let timestamp: number = Math.floor(pos.x);
            return this.plotOptions.grid.markings.some((marking: {xaxis: {from: number, to: number}}) => {
                const range: number = this.currentTimeWindow.end - this.currentTimeWindow.start;
                const scopeAround: number = range * HistoricalTrendingController.AROUND_MARKING_MODIFIER;
                return (marking.xaxis.from - scopeAround <= timestamp && marking.xaxis.to + scopeAround >= timestamp);
            });
        }

        private isMarkingBelow(pos: jquery.flot.pos, markingTimestamp: number): boolean {
            let timestamp: number = Math.floor(pos.x);
            for (let marking of this.plotOptions.grid.markings) {
                const range: number = this.currentTimeWindow.end - this.currentTimeWindow.start;
                const scopeAround: number = range * HistoricalTrendingController.AROUND_MARKING_MODIFIER;
                if (marking.xaxis.from - scopeAround <= timestamp && marking.xaxis.to + scopeAround >= timestamp &&
                    marking.xaxis.from === markingTimestamp) {
                    return true;
                }
            }
            return false;
        }

        private getTimestampOfFirstMarkingBelow(pos: jquery.flot.pos): number {
            for (let marking of this.plot.getOptions().grid.markings) {
                if (this.isMarkingBelow(pos, marking.xaxis.from)) {
                    return marking.xaxis.from;
                }
            }
            return undefined;
        }

        private setPlotOptions(): void {
            this.plotOptions = {
                colors: ['#22C969'],
                series: {
                    lines: {
                        show: true,
                        lineWidth: 2.0,
                        fill: true,
                    },
                    curvedLines: {
                        active: true,
                        apply: true,
                    },
                },
                grid: {
                    hoverable: true,
                    clickable: true,
                    backgroundColor: "rgb(51, 51, 51,0.95)",
                    color: {
                        top: "rgb(51, 51, 51,0.85)",
                        right: "rgb(51, 51, 51,0.85)",
                        bottom: "rgb(51, 51, 51,0.85)",
                        left: "rgb(51, 51, 51,0.85)",
                    },
                    markings: [],
                },
                axisLabels: {
                    show: true,
                    color: 'lightslategray',
                    axisLabelColour: 'lightslategray',
                },
                yaxes: [{
                    position: 'left',
                    axisLabel: 'Mbps',
                    axisLabelPadding: 25,
                    axisLabelFontSizePixels: 12,
                    axisLabelColour: "lightslategray",
                },
                ],
                xaxis: {
                    mode: "time",
                    // timezone: "browser",
                    timeformat: '%d/%m/%Y<br/>%H:%M',
                    tickSize: [1, "hour"],
                    axisLabelUseCanvas: true,
                    tickColor: "gray",
                    color: 'lightslategray',
                    axisLabelColour: 'lightslategray',
                },
                yaxis: {
                    axisLabelUseCanvas: true,
                    tickColor: "gray",
                    min: 0,
                    panRange: false,
                },
                pan: {
                    interactive: true,
                },
            };
        }

        private setYAxisLabel(): void {
            switch (this.element.unit) {
                case '%':
                    this.plotOptions.yaxes[0].axisLabel = '%';
                    break;
                case 'pkts/s':
                    this.plotOptions.yaxes[0].axisLabel = 'pkts/s';
                    break;
                case 'ms':
                case 'us':
                    this.plotOptions.yaxes[0].axisLabel = 'ms';
                    break;
                // case 'bps':
                // case 'kbps':
                default:
                    this.plotOptions.yaxes[0].axisLabel = 'Mbps';
            }
        }

        /*
        * Initial load of the element to be represented. Matching all routes:
        *    /historical/:streamId
        *    /historicalByClient/:clientId/:blockId?
        *    /historicalByClass/:class/:id/:param
        *    /historicalByDataPort/:sourceId/:sinkId/:param
        */
        private loadTrendingElement(): ng.IPromise<void> {
            if (this.$routeParams.streamId) {
                return this.loadStreamPipeElementInfoAndInitialTrendingData();
            } else if (this.$routeParams.clientId) {
                return this.loadNetworkNodeElementInfoAndInitialTrendingData();
            } else if (this.$routeParams.class === 'sonLinks') {
                return this.loadSonLinkElementInfoAndInitialTrendingData();
            }
            else if (this.$routeParams.class === 'Digicon') {
                this.loadDigiconInfoAndInitialTrendingData();
                return this.$q.resolve();
            }
            else if (this.$routeParams.sourceId || this.$routeParams.sinkId) {
                this.loadSonDataPortInfoAndInitialTrendingData();
                return this.$q.resolve();
            } else {
                this.element = undefined;
                return this.$q.resolve();
            }
        }

        private loadStreamPipeElementInfoAndInitialTrendingData(): ng.IPromise<void> {
            this.element = {
                type: 'stream',
                param: 'bitrate',
                id: parseInt(this.$routeParams.streamId, 10),
                unit: 'bps',
            };
            return this.streamPipeService
                .getStreamPipeBlockConnectionsByStreamId(this.element.id)
                .then((elem: Models.IStreamPipeBlockConnectionInfo[]) => {
                    if (elem.length > 0) {
                        const arrowForward: string = ' <i class="material-icons">arrow_forward</i> ';
                        const origin: string = elem[0]!.origBlockConnection!.blockName;
                        const destination: string = elem[elem.length - 1]!.destBlockConnection!.blockName;
                        this.title = origin + arrowForward + destination;
                        this.loadTrendingData();
                    } else {
                        this.$location.path('/multiviewer');
                    }
                });
        }

        private loadNetworkNodeElementInfoAndInitialTrendingData(): ng.IPromise<void> {
            const nodeId: number =  parseInt(this.$routeParams.clientId, 10);
            const blockId: number = parseInt(this.$routeParams.blockId, 10);
            return this.blockService.blocksInNode(nodeId)
                .then((blocks: Models.Block[]) => {
                    if (blocks && blocks.length > 0) {
                        this.blocks = blocks.filter((block: Models.Block) => {
                            if (blockId) {
                                return block.blockType.naturalKey !== BlockTypeNaturalKey.MVP_SERVER && block.id === blockId;
                            }
                            return block.blockType.naturalKey !== BlockTypeNaturalKey.MVP_SERVER;
                        });

                        if (this.blocks.length > 0 && this.blocks[0].blockTypeNaturalKey === BlockTypeNaturalKey.SRT_GW_RX_LSTN) {
                            this.params = [];
                            let param: IParam = {
                                href: `blocks/${blockId}`,
                                param: 'bitrate',
                                title: 'Bitrate',
                            };
                            this.params.push(param);
                            param = {
                                href: `blocks/${blockId}`,
                                param: 'probedBitrate',
                                title: 'Probed Bitrate',
                            };
                            this.params.push(param);

                            this.selectedParam = this.params[0];
                            this.element = {
                                type: 'networkNode',
                                param: this.selectedParam.param,
                                elementHref: this.selectedParam.href,
                                unit: 'bps',
                            };
                        } else {
                            this.element = {
                                type: 'networkNode',
                                param: 'bitrate',
                                id: nodeId,
                                unit: 'bps',
                            };
                        }

                        this.blockNames = this.blocks
                            .sort((b1: Models.Block, b2: Models.Block) => b1.id - b2.id)
                            .map((block: Models.Block) => block.name);
                        if (this.blocks.length > 0) {
                            this.selectBlockToShow(this.blocks[0]);
                        }
                    }
                });
        }

        private loadSonLinkElementInfoAndInitialTrendingData(): ng.IPromise<void> {
            let unit: Unit;
            if (this.$routeParams.param === 'load') {
                unit = '%';
            } else if (this.$routeParams.param === 'latency') {
                unit = 'us';
            } else if (this.$routeParams.param === 'bitrate') {
                unit = 'bps';
            }else {
                unit = 'pkts/s';
            }
            this.element = {
                type: 'sonLink',
                param: this.$routeParams.param,
                id: parseInt(this.$routeParams.id, 10),
                unit: unit,
            };
            return this.sonAdapterService.getSonLinkById(this.element.id).then((res: Models.ISonLink) => {
                const arrowForward: string = ' <i class="material-icons">arrow_forward</i> ';
                const param: string = this.element.param !== 'latency' ? this.element.param : 'control packet roundtrip';
                this.title = `SON link: ${res.name.replace('->', arrowForward)}<br>Parameter: ${param}`;
                this.loadTrendingData();
            });
        }

        private loadDigiconInfoAndInitialTrendingData(): void {
            this.params = [];
            if (this.$routeParams.sourceId) {
                const digiconId: string = this.$routeParams.sourceId;
                if (digiconId !== "_") {
                    const param: IParam = {
                        href: `digicon/${digiconId}`,
                        param: this.$routeParams.param,
                        title: this.$routeParams.title,
                    };
                    this.params.push(param);
                }
            }

            this.selectedParam = this.params[0];
            this.element = {
                type: 'digicon',
                param: this.selectedParam.param,
                elementHref: this.selectedParam.href,
                unit: 'bps',
            };
            this.title = this.selectedParam.title;
            this.loadTrendingData();

        }

        private loadSonDataPortInfoAndInitialTrendingData(): void {
            this.params = [];
            if (this.$routeParams.sourceId) {
                const sourceDns: string = this.$routeParams.sourceId;
                if (sourceDns !== "_") {
                    const param: IParam = {
                        href: `sonElements/${sourceDns}`,
                        param: this.$routeParams.param,
                        title: sourceDns,
                    };
                    this.params.push(param);
                }
            }
            if (this.$routeParams.sinkId) {
                const sinkDns: string = this.$routeParams.sinkId;
                if (sinkDns !== "_") {
                    const param: IParam = {
                        href: `sonElements/${sinkDns}`,
                        param: this.$routeParams.param,
                        title: sinkDns,
                    };
                    this.params.push(param);
                }
            }

            this.selectedParam = this.params[0];
            this.element = {
                type: 'sonElements',
                param: this.selectedParam.param,
                elementHref: this.selectedParam.href,
                unit: 'bps',
            };
            this.title = this.selectedParam.title;
            this.loadTrendingData();

        }

        private loadTrendingData(interval?: {start: number, end: number}): Promise<void> {
            let firstLoad: boolean = !interval;
            if (firstLoad) {
                interval = {
                    start: this.initialDateValue - HistoricalTrendingController.INITIAL_DATA_MSECONDS,
                    end: this.initialDateValue,
                };
            } else {
                this.trendingRefreshing = true;
            }
            this.currentInterval = interval;
            let secs: number = this.calculateSecs(interval.start, interval.end);
            return this.requestTrending(firstLoad, interval, secs);            
        }

        private async requestTrending(firstLoad: boolean, interval: { start: number, end: number }, secs: number): Promise<void> {
            if (this.element.type === 'stream') {
                return this.streamStatusService.getStreamPipeTrendingDataFromSecondsAndEnd(this.element.id, secs, interval.end)
                    .then((trending: Models.ITrendingResult) => {
                        this.processTrending(trending, firstLoad, secs, interval);
                    }).finally(() => {
                        this.trendingRefreshing = false;
                    });
            } else if (this.element.type === 'networkNode' && this.element.blockId && !this.params) {
                return this.streamStatusService.getBlockTrendingDataFromSecondsAndEnd(this.element.blockId, secs, interval.end)
                    .then((trending: Models.ITrendingResult) => {
                        this.processTrending(trending, firstLoad, secs, interval);
                    }).finally(() => {
                        this.trendingRefreshing = false;
                    });
            } else if (this.element.type === 'networkNode' && this.element.blockId && this.params && this.params.length > 0) {
                return this.streamStatusService.getParamTrendingData(`blocks/${this.element.blockId}`,
                    this.element.param, interval.end, secs)
                    .then((trending: Models.ITrendingResult) => {
                        this.processTrending(trending, firstLoad, secs, interval);
                    }).finally(() => {
                        this.trendingRefreshing = false;
                    });
            } else if (this.element.type === 'sonLink') {
                return this.streamStatusService.getParamTrendingData(`sonLinks/${this.element.id}`, this.element.param, interval.end, secs)
                    .then((trending: Models.ITrendingResult) => {
                        this.processTrending(trending, firstLoad, secs, interval);
                    }).finally(() => {
                        this.trendingRefreshing = false;
                    });
            } else if (this.element.type === 'sonElements') {
                return this.streamStatusService
                    .getParamTrendingData(this.element.elementHref, this.element.param, interval.end, secs)
                    .then((trending: Models.ITrendingResult) => {
                        this.processTrending(trending, firstLoad, secs, interval);
                    }).finally(() => {
                        this.trendingRefreshing = false;
                    });
            } else if (this.element.type === 'digicon') {
                let trendingBitrates: Models.ITrendingResult[] = [];
                for (let i = 0; i < this.bitrates.length; i++) {
                    trendingBitrates[i] = await this.streamStatusService.getParamTrendingData(this.element.elementHref, this.bitrates[i], interval.end, secs);
                }
                this.processTrendingDigicon(trendingBitrates, firstLoad, secs, interval);
                this.trendingRefreshing = false;
                return this.$q.resolve();


            } else {
                return this.$q.resolve();
            }
        }

        private processTrending(trending: Models.ITrendingResult,
                                firstLoad: boolean, 
                                secs: number, 
                                interval?: {start: number, end: number}): void {
            let bitrate: Models.IBitRateTrending[] = [];
            if (trending!.values!.length > 0) {
                this.element.param = trending.paramName;
                bitrate = trending.values;

            } else  {
                bitrate = Controllers.HistoricalTrendingController.generateZeroBitrate(secs, interval.end);
            }
            this.processDataPriorToPlot(bitrate, firstLoad);
            if (!this.events) {
                this.loadEvents();
            }

        }

        private processTrendingDigicon(trending: Models.ITrendingResult[],
                                firstLoad: boolean,
                                secs: number,
                                interval?: {start: number, end: number}): void {
            let bitrate: Models.IBitRateTrending[][] = [];
            for (let i = 0; i < trending.length; i++) {
                if (trending[i]!.values!.length > 0) {
                    this.element.param = trending[i].paramName;
                    this.bitratesLabel.push(trending[i].paramName);
                    bitrate[i] = trending[i].values;

                } else  {
                    bitrate[i] = Controllers.HistoricalTrendingController.generateZeroBitrate(secs, interval.end);
                }
            }
            this.processDigiconDataPriorToPlot(bitrate, firstLoad);

            // no events for digicon for the moment
            //if (!this.events) {
              //  this.loadEvents();
            //}

        }

        private loadEvents(): ng.IPromise<void> {
            if (this.$rootScope.flexSections.getSectionWithId(21).canRead) {
                let sevenDaysAgo: number = this.initialDateValue - new Date(HistoricalTrendingController.MAX_TIME_RANGE_MSECS).getTime();
                let now: Date = new Date(this.initialDateValue);
                if (this.element.type === 'stream') {
                    return this.alarmsService.findEventsForStreamPipe(this.element.id, new Date(sevenDaysAgo), now, true)
                        .then((events: Models.IPaginatedList<Models.FlexEvent>) => {
                            this.events = events.elements;
                            this.createAlarmsMarks(true);
                        }).catch(() => { this.events = []; });
                } else if (this.element.type === 'networkNode') {
                    return this.alarmsService.findAllFromBlockId(this.element.blockId, new Date(sevenDaysAgo), now, true)
                        .then((events: Models.IPaginatedList<Models.FlexEvent>) => {
                            this.events = events.elements;
                            this.createAlarmsMarks(true);
                        }).catch(() => { this.events = []; });
                } else if (this.element.type === 'sonLink') {
                    return this.alarmsService.findEventsForSonLinkId(this.element.id)
                        .then((events: Models.FlexEvent[]) => {
                            this.events = events;
                            this.createAlarmsMarks(true);
                        }).catch(() => { this.events = []; });
                } else if (this.element.type === 'sonElements') {
                    this.events = [];
                    return this.alarmsService.findEventsForHref(this.selectedParam.href)
                        .then((events: Models.FlexEvent[]) => {
                            this.events = events;
                            this.createAlarmsMarks(true);
                        }).catch(() => { this.events = []; });
                } else {
                    return this.$q.resolve();
                }
            }
        }

        private hasSomeMinOrMax(data: Models.IBitRateTrending[]): boolean {
            for (let point of data) {
                if (point.minValue !== undefined || point.maxValue !== undefined) {
                    return true;
                }
            }
            return false;
        }

        private transformUnit(value: number): number {
            switch (this.element.unit) {
                case 'bps':
                    return this.bitsToMpbs(value);
                case 'kbps':
                    return value !== null ? this.bitsToMpbs(value * 1000) : null;
                case '%':
                    return value;
                case 'pkts/s':
                    return value;
                case 'ms':
                    return value;
                case 'us':
                    return value * 1000;
                default:
                    return value;
            }
        }

        private processDataPriorToPlot(data: Models.IBitRateTrending[], firstLoad: boolean): void {
            this.dataToPlot = {
                minDataToPlot: undefined,
                avgDataToPlot: undefined,
                maxDataToPlot: undefined,
            };
            let offset: string = this.timezoneService.getEffectiveOffset();

            // let minStep: number = undefined;

            const hasSomeMinOrMax: boolean = this.hasSomeMinOrMax(data);
            const minValuesData: number[][] = [];
            const avgValuesData: number[][] = [];
            const maxValuesData: number[][] = [];
            let lastIsNull: boolean = false;

            for (let i: number = 0; i < data.length; i++) {

                const dateOffseted: number = new Date(data[i].date.slice(0, -5) + offset).valueOf();
                // min sampling values
                if (data[i].minValue !== undefined) {
                    minValuesData.push([dateOffseted, this.transformUnit(data[i].minValue)]);
                } else if (hasSomeMinOrMax && data[i].bitrate === 0) {
                    minValuesData.push([dateOffseted, 0]);
                }
                // avg sampling values
                const convertedAverage: number = this.transformUnit(data[i].bitrate);

                if (convertedAverage === null) {
                    lastIsNull = true;
                } else {
                    // Add 0 after null to avoid joining graph
                    if (lastIsNull && convertedAverage !== 0)
                        avgValuesData.push([dateOffseted - 1, 0]);
                    lastIsNull = false;
                }
                avgValuesData.push([dateOffseted, convertedAverage]);

                // max sampling values
                if (data[i].maxValue !== undefined) {
                    maxValuesData.push([dateOffseted, this.transformUnit(data[i].maxValue)]);
                } else if (hasSomeMinOrMax && data[i].bitrate === 0) {
                    maxValuesData.push([dateOffseted, 0]);
                }
            }

            this.dataToPlot.minDataToPlot = {
                data: minValuesData,
                label: 'min sampling',
                color: '#ababab',
                lines: {
                    fill: false,
                    lineWidth: 1,
                },
                points: { show: false },
            };

            this.dataToPlot.avgDataToPlot = {
                data: avgValuesData,
                label: 'avg sampling',
                color: 'rgb(34,211, 105,0.7)',
                lines: {
                    fill: true,
                    fillColor: 'rgb(54, 121, 83,0.7)',
                },
                points: { show: false },
            };

            this.dataToPlot.maxDataToPlot = {
                data: maxValuesData,
                label: 'max sampling',
                color: 'green',
                lines: {
                    fill: false,
                },
                points: { show: false },
            };

            const start: number = this.dataToPlot.avgDataToPlot.data[0][0];
            const end: number = this.dataToPlot.avgDataToPlot.data[this.dataToPlot.avgDataToPlot.data.length - 1][0];

            this.currentTimeWindow = {
                start: start,
                end: end,
                size: Common.Utils.hoursToDays((end - start) / 1000 / 3600),
            };
            this.currentTimeWindowUtc = {
                start: this.applyOffset(this.currentTimeWindow.start, true),
                end: this.applyOffset(this.currentTimeWindow.end, true),
                size: undefined,
            };
            if (firstLoad) {
                const xMin: number = this.initialDateValue -  HistoricalTrendingController.MAX_TIME_RANGE_MSECS;
                const xMax: number = this.dataToPlot.avgDataToPlot.data[this.dataToPlot.avgDataToPlot.data.length - 1][0];
                this.plotOptions.xaxis.panRange = [xMin, xMax];

                const currentMinutes: number = (this.currentTimeWindow.end - this.currentTimeWindow.start) / 1000 / 60;
                for (let opt of this.quickZoomLinks) {
                    if (opt.minutes >= currentMinutes - currentMinutes * .2 && opt.minutes <= currentMinutes + currentMinutes * .2) {
                        this.currentMinutes = opt.minutes;
                    }
                }
            }

            this.updateStatsAndYMax();
            this.plotTrending();
        }

        private processDigiconDataPriorToPlot(data: Models.IBitRateTrending[][], firstLoad: boolean): void {
            this.dataToPlotDigicon = {
                dataToPlotTrunk: undefined,
                dataToPlotFiles: undefined,
                dataToPlotAperi: undefined,
                dataToPlot360: undefined,
                dataToPlotEN07: undefined,
                dataToPlotEN08: undefined,
            };
            let offset: string = this.timezoneService.getEffectiveOffset();

            const dataToPlotTrunk: number[][] = [];
            const dataToPlotFiles: number[][] = [];
            const dataToPlotAperi: number[][] = [];
            const dataToPlot360: number[][] = [];
            const dataToPlotEN07: number[][] = [];
            const dataToPlotEN08: number[][] = [];

            for (let k: number = 0; k < data.length; k++) {

                let lastIsNull: boolean = false;
                for (let i: number = 0; i < data[k].length; i++) {
                    const dateOffseted: number = new Date(data[k][i].date.slice(0, -5) + offset).valueOf();
                    const convertedAverage: number = this.transformUnit(data[k][i].bitrate);

                    // trunk_bitrate values
                    if (this.bitratesLabel[k] === "trunk_bitrate") {

                        if (convertedAverage === null) {
                            lastIsNull = true;
                        } else {
                            // Add 0 after null to avoid joining graph
                            if (lastIsNull && convertedAverage !== 0)
                                dataToPlotTrunk.push([dateOffseted - 1, 0]);
                            lastIsNull = false;
                        }
                        dataToPlotTrunk.push([dateOffseted, convertedAverage]);
                    }

                    // files_bitrate values
                    if (this.bitratesLabel[k] === "files_bitrate") {

                        if (convertedAverage === null) {
                            lastIsNull = true;
                        } else {
                            // Add 0 after null to avoid joining graph
                            if (lastIsNull && convertedAverage !== 0)
                                dataToPlotFiles.push([dateOffseted - 1, 0]);
                            lastIsNull = false;
                        }
                        dataToPlotFiles.push([dateOffseted, convertedAverage]);
                    }

                    // 360_bitrate values
                    if (this.bitratesLabel[k] === "360_bitrate") {

                        if (convertedAverage === null) {
                            lastIsNull = true;
                        } else {
                            // Add 0 after null to avoid joining graph
                            if (lastIsNull && convertedAverage !== 0)
                                dataToPlot360.push([dateOffseted - 1, 0]);
                            lastIsNull = false;
                        }
                        dataToPlot360.push([dateOffseted, convertedAverage]);
                    }

                    // aperi_bitrate values
                    if (this.bitratesLabel[k] === "aperi_bitrate") {

                        if (convertedAverage === null) {
                            lastIsNull = true;
                        } else {
                            // Add 0 after null to avoid joining graph
                            if (lastIsNull && convertedAverage !== 0)
                                dataToPlotAperi.push([dateOffseted - 1, 0]);
                            lastIsNull = false;
                        }
                        dataToPlotAperi.push([dateOffseted, convertedAverage]);
                    }

                    // EN07_bitrate values
                    if (this.bitratesLabel[k] === "EN07_bitrate") {

                        if (convertedAverage === null) {
                            lastIsNull = true;
                        } else {
                            // Add 0 after null to avoid joining graph
                            if (lastIsNull && convertedAverage !== 0)
                                dataToPlotEN07.push([dateOffseted - 1, 0]);
                            lastIsNull = false;
                        }
                        dataToPlotEN07.push([dateOffseted, convertedAverage]);
                    }

                    // EN08_bitrate values
                    if (this.bitratesLabel[k] === "EN08_bitrate") {

                        if (convertedAverage === null) {
                            lastIsNull = true;
                        } else {
                            // Add 0 after null to avoid joining graph
                            if (lastIsNull && convertedAverage !== 0)
                                dataToPlotEN08.push([dateOffseted - 1, 0]);
                            lastIsNull = false;
                        }
                        dataToPlotEN08.push([dateOffseted, convertedAverage]);
                    }
                }
            }

            this.dataToPlotDigicon.dataToPlotAperi = {
                data: dataToPlotAperi,
                label: 'aperi_bitrate',
                color: '#9ceeee',
                lines: {
                    fill: false,
                    lineWidth: 1,
                },
                points: { show: false },
            };

            this.dataToPlotDigicon.dataToPlotTrunk = {
                data: dataToPlotTrunk,
                label: 'trunk_bitrate',
                color: '#ffffff',
                lines: {
                    fill: false,
                    fillColor: 'rgb(54, 121, 83,0.7)',
                },
                points: { show: false },
            };

            this.dataToPlotDigicon.dataToPlot360 = {
                data: dataToPlot360,
                label: '360_bitrate',
                color: '#c9a4ee',
                lines: {
                    fill: false,
                },
                points: { show: false },
            };

            this.dataToPlotDigicon.dataToPlotFiles = {
                data: dataToPlotFiles,
                label: 'files_bitrate',
                color: '#eeee9c',
                lines: {
                    fill: false,
                },
                points: { show: false },
            };

            this.dataToPlotDigicon.dataToPlotEN07 = {
                data: dataToPlotEN07,
                label: 'FEED_A_UHD_bitrate',
                color: '#7574fa',
                lines: {
                    fill: false,
                    lineWidth: 1,
                },
                points: { show: false },
            };

            this.dataToPlotDigicon.dataToPlotEN08 = {
                data: dataToPlotEN08,
                label: 'FEED_D_UHD_bitrate',
                color: '#f26160',
                lines: {
                    fill: false,
                    lineWidth: 1,
                },
                points: { show: false },
            };


            const start: number = this.dataToPlotDigicon.dataToPlotTrunk.data[0][0];
            const end: number = this.dataToPlotDigicon.dataToPlotTrunk.data[this.dataToPlotDigicon.dataToPlotTrunk.data.length - 1][0];

            this.currentTimeWindow = {
                start: start,
                end: end,
                size: Common.Utils.hoursToDays((end - start) / 1000 / 3600),
            };
            this.currentTimeWindowUtc = {
                start: this.applyOffset(this.currentTimeWindow.start, true),
                end: this.applyOffset(this.currentTimeWindow.end, true),
                size: undefined,
            };

            if (firstLoad) {
                const xMin: number = this.initialDateValue -  HistoricalTrendingController.MAX_TIME_RANGE_MSECS;
                const xMax: number = this.dataToPlotDigicon.dataToPlotTrunk.data[this.dataToPlotDigicon.dataToPlotTrunk.data.length - 1][0];
                this.plotOptions.xaxis.panRange = [xMin, xMax];

                const currentMinutes: number = (this.currentTimeWindow.end - this.currentTimeWindow.start) / 1000 / 60;
                for (let opt of this.quickZoomLinks) {
                    if (opt.minutes >= currentMinutes - currentMinutes * .2 && opt.minutes <= currentMinutes + currentMinutes * .2) {
                        this.currentMinutes = opt.minutes;
                    }
                }
            }


            this.updateStatsAndYMaxDigicon();
            this.plotTrending();
        }

        private updateStatsAndYMaxDigicon(): void {
            this.stats = {};
            let stats1: IStats = {};
            let stats2: IStats = {};
            let stats3: IStats = {};
            let stats4: IStats = {};
            let stats5: IStats = {};
            let stats6: IStats = {};

            this.statsDigicon = [stats1, stats2, stats3, stats4, stats5, stats6];

            //360 bitrate
            this.statsDigicon[0].min = this.dataToPlotDigicon.dataToPlot360.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] < curr[1] ? prev : curr;
                })[1];
            this.statsDigicon[0].max = this.dataToPlotDigicon.dataToPlot360.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] > curr[1] ? prev : curr;
                })[1];

            this.statsDigicon[0].avg = this.dataToPlotDigicon.dataToPlot360.data
                .map((curr: number[]) => {
                    return curr[1];
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlot360.data.length;

            this.statsDigicon[0].var = this.dataToPlotDigicon.dataToPlot360.data
                .map((value: number[]) => {
                    return Math.abs(value[1] - this.statsDigicon[0].avg);
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlot360.data.length;
            this.statsDigicon[0].varPerc = this.statsDigicon[0].var !== 0 ? this.statsDigicon[0].var * 100 / this.statsDigicon[0].avg : 0;


            //files_bitrate
            this.statsDigicon[1].min = this.dataToPlotDigicon.dataToPlotFiles.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] < curr[1] ? prev : curr;
                })[1];
            this.statsDigicon[1].max = this.dataToPlotDigicon.dataToPlotFiles.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] > curr[1] ? prev : curr;
                })[1];

            this.statsDigicon[1].avg = this.dataToPlotDigicon.dataToPlotFiles.data
                .map((curr: number[]) => {
                    return curr[1];
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotFiles.data.length;

            this.statsDigicon[1].var = this.dataToPlotDigicon.dataToPlotFiles.data
                .map((value: number[]) => {
                    return Math.abs(value[1] - this.statsDigicon[1].avg);
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotFiles.data.length;
            this.statsDigicon[1].varPerc = this.statsDigicon[1].var !== 0 ? this.statsDigicon[1].var * 100 / this.statsDigicon[1].avg : 0

            //aperi_bitrate
            this.statsDigicon[2].min = this.dataToPlotDigicon.dataToPlotAperi.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] < curr[1] ? prev : curr;
                })[1];
            this.statsDigicon[2].max = this.dataToPlotDigicon.dataToPlotAperi.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] > curr[1] ? prev : curr;
                })[1];

            this.statsDigicon[2].avg = this.dataToPlotDigicon.dataToPlotAperi.data
                .map((curr: number[]) => {
                    return curr[1];
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotAperi.data.length;

            this.statsDigicon[2].var = this.dataToPlotDigicon.dataToPlotAperi.data
                .map((value: number[]) => {
                    return Math.abs(value[1] - this.statsDigicon[2].avg);
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotAperi.data.length;
            this.statsDigicon[2].varPerc = this.statsDigicon[2].var !== 0 ? this.statsDigicon[2].var * 100 / this.statsDigicon[2].avg : 0;

            //trunk_bitrate
            this.statsDigicon[3].min = this.dataToPlotDigicon.dataToPlotTrunk.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] < curr[1] ? prev : curr;
                })[1];
            this.statsDigicon[3].max = this.dataToPlotDigicon.dataToPlotTrunk.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] > curr[1] ? prev : curr;
                })[1];

            this.statsDigicon[3].avg = this.dataToPlotDigicon.dataToPlotTrunk.data
                .map((curr: number[]) => {
                    return curr[1];
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotTrunk.data.length;

            this.statsDigicon[3].var = this.dataToPlotDigicon.dataToPlotTrunk.data
                .map((value: number[]) => {
                    return Math.abs(value[1] - this.statsDigicon[3].avg);
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotTrunk.data.length;
            this.statsDigicon[3].varPerc = this.statsDigicon[3].var !== 0 ? this.statsDigicon[3].var * 100 / this.statsDigicon[3].avg : 0;


            //EN07_bitrate
            this.statsDigicon[4].min = this.dataToPlotDigicon.dataToPlotEN07.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] < curr[1] ? prev : curr;
                })[1];
            this.statsDigicon[4].max = this.dataToPlotDigicon.dataToPlotEN07.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] > curr[1] ? prev : curr;
                })[1];

            this.statsDigicon[4].avg = this.dataToPlotDigicon.dataToPlotEN07.data
                .map((curr: number[]) => {
                    return curr[1];
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotEN07.data.length;

            this.statsDigicon[4].var = this.dataToPlotDigicon.dataToPlotEN07.data
                .map((value: number[]) => {
                    return Math.abs(value[1] - this.statsDigicon[4].avg);
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotEN07.data.length;
            this.statsDigicon[4].varPerc = this.statsDigicon[4].var !== 0 ? this.statsDigicon[4].var * 100 / this.statsDigicon[4].avg : 0;

            //EN08_bitrate
            this.statsDigicon[5].min = this.dataToPlotDigicon.dataToPlotEN08.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] < curr[1] ? prev : curr;
                })[1];
            this.statsDigicon[5].max = this.dataToPlotDigicon.dataToPlotEN08.data
                .reduce((prev: number[], curr: number[]) => {
                    return prev[1] > curr[1] ? prev : curr;
                })[1];

            this.statsDigicon[5].avg = this.dataToPlotDigicon.dataToPlotEN08.data
                .map((curr: number[]) => {
                    return curr[1];
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotEN08.data.length;

            this.statsDigicon[5].var = this.dataToPlotDigicon.dataToPlotEN08.data
                .map((value: number[]) => {
                    return Math.abs(value[1] - this.statsDigicon[5].avg);
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlotDigicon.dataToPlotEN08.data.length;
            this.statsDigicon[5].varPerc = this.statsDigicon[5].var !== 0 ? this.statsDigicon[4].var * 100 / this.statsDigicon[5].avg : 0;
            this.plotOptions.yaxis.max = Math.max(this.statsDigicon[0].max, this.statsDigicon[1].max, this.statsDigicon[2].max, this.statsDigicon[3].max) * 2;

        }


        private updateStatsAndYMax(): void {
            this.stats = {};
            if (this.dataToPlot.minDataToPlot.data.length > 0) {
                this.stats.min = this.dataToPlot.minDataToPlot.data
                    .reduce((prev: number[], curr: number[]) => {
                        return prev[1] < curr[1] ? prev : curr;
                    })[1];
            } else {
                this.stats.min = this.dataToPlot.avgDataToPlot.data
                    .reduce((prev: number[], curr: number[]) => {
                        return prev[1] < curr[1] ? prev : curr;
                    })[1];
            }

            this.stats.avg = this.dataToPlot.avgDataToPlot.data
                .map((curr: number[]) => {
                    return curr[1];
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlot.avgDataToPlot.data.length;

            if (this.dataToPlot.maxDataToPlot.data.length > 0) {
                this.stats.max = this.dataToPlot.maxDataToPlot.data
                    .reduce((prev: number[], curr: number[]) => {
                        return prev[1] > curr[1] ? prev : curr;
                    })[1];
            } else {
                this.stats.max = this.dataToPlot.avgDataToPlot.data
                    .reduce((prev: number[], curr: number[]) => {
                        return prev[1] > curr[1] ? prev : curr;
                    })[1];
            }

            this.stats.var = this.dataToPlot.avgDataToPlot.data
                .map((value: number[]) => {
                    return Math.abs(value[1] - this.stats.avg);
                }).reduce((prev: number, curr: number) => {
                    return prev + curr;
                }) / this.dataToPlot.avgDataToPlot.data.length;
            this.stats.varPerc = this.stats.var !== 0 ? this.stats.var * 100 / this.stats.avg : 0;

            this.plotOptions.yaxis.max = this.stats.max * 1.2;
        }

        private plotTrending(): void {


            let dataToPlot: IDataSerie[] = [];
            if (this.element.type === "digicon") {
                if (!this.dataToPlotDigicon)
                    return;

                if (this.dataToPlotDigicon.dataToPlot360) dataToPlot.push(this.dataToPlotDigicon.dataToPlot360);
                if (this.dataToPlotDigicon.dataToPlotFiles) dataToPlot.push(this.dataToPlotDigicon.dataToPlotFiles);
                if (this.dataToPlotDigicon.dataToPlotAperi) dataToPlot.push(this.dataToPlotDigicon.dataToPlotAperi);
                if (this.dataToPlotDigicon.dataToPlotTrunk) dataToPlot.push(this.dataToPlotDigicon.dataToPlotTrunk);
                if (this.dataToPlotDigicon.dataToPlotEN07) dataToPlot.push(this.dataToPlotDigicon.dataToPlotEN07);
                if (this.dataToPlotDigicon.dataToPlotEN08) dataToPlot.push(this.dataToPlotDigicon.dataToPlotEN08);

                this.plotOptions.legend = { show: true, backgroundColor: 'lightgrey' };

            }
            else {
                if (!this.dataToPlot || !this.dataToPlot.avgDataToPlot)
                    return;

                if (this.dataToPlot.maxDataToPlot) dataToPlot.push(this.dataToPlot.maxDataToPlot);
                if (this.dataToPlot.avgDataToPlot) dataToPlot.push(this.dataToPlot.avgDataToPlot);
                if (this.dataToPlot.minDataToPlot) dataToPlot.push(this.dataToPlot.minDataToPlot);

                if (this.dataToPlot.maxDataToPlot.data.length > 0 || this.dataToPlot.minDataToPlot.data.length > 0) {
                    this.plotOptions.legend = { show: true, backgroundColor: 'lightgrey' };
                } else {
                    this.plotOptions.legend = { show: false };
                }
            }


            this.adjustXAxisAndTickSize();
            if (this.events && this.element.type !== "digicon") {
                this.createAlarmsMarks();
            }
            this.plot = $.plot($('#historical-stream'), dataToPlot, this.plotOptions);
            this.trendingLoaded = true;
        }

        private createAlarmsMarks(firstTime?: boolean): void {
            const startUnoffseted: number = this.applyOffset(this.plotOptions.xaxis.min as number, true);
            const endUnoffseted: number = this.applyOffset(this.plotOptions.xaxis.max as number, true);
            const criticalAlarms: Models.FlexEvent[] = this.events.filter((event: Models.FlexEvent) => {
                const eventTimestamp: number = new Date(event.timestamp).valueOf();
                return event.component === Models.ALARMS_COMPONENT && event.operation === Models.CREATE_ALARM_OPERATION &&
                    eventTimestamp >= startUnoffseted && eventTimestamp <= endUnoffseted &&
                    (event.data.level === 0 || event.data.level === 8);
            });
            this.plotOptions.grid.markings = [];
            for (let alarm of criticalAlarms) {
                this.plotOptions.grid.markings.push({
                    xaxis: {
                        from: this.applyOffset(new Date(alarm.timestamp).valueOf()),
                        to: this.applyOffset(new Date(alarm.timestamp).valueOf()),
                    },
                    color: "red",
                    data: [alarm.data],
                });
            }

            if (firstTime) {
                this.plotTrending();
            }
        }

        private adjustXAxisAndTickSize(): void {
            let start: number;
            let end: number;
            if (this.element.type !== "digicon") {
                start = this.dataToPlot.avgDataToPlot.data[0][0];
                end = this.dataToPlot.avgDataToPlot.data[this.dataToPlot.avgDataToPlot.data.length - 1][0];
            }
            else {
                start = this.dataToPlotDigicon.dataToPlotTrunk.data[0][0];
                end = this.dataToPlotDigicon.dataToPlotTrunk.data[this.dataToPlotDigicon.dataToPlotTrunk.data.length - 1][0];
            }

            this.plotOptions.xaxis.min = start;
            this.plotOptions.xaxis.max = end;

            const minutes: number = Math.floor((end - start) / 1000 / 60);
            if (minutes < 15) {
                this.plotOptions.xaxis.tickSize = [1, "minute"];
            } else if (minutes <= 30) {
                this.plotOptions.xaxis.tickSize = [3, "minute"];
            } else if (minutes <= 60) {
                this.plotOptions.xaxis.tickSize = [5, "minute"];
            } else if (minutes <= 3 * 60) {
                this.plotOptions.xaxis.tickSize = [10, "minute"];
            } else if (minutes <= 6 * 60) {
                this.plotOptions.xaxis.tickSize = [30, "minute"];
            } else if (minutes <= 12 * 60) {
                this.plotOptions.xaxis.tickSize = [1, "hour"];
            } else if (minutes <= 25 * 60) {
                this.plotOptions.xaxis.tickSize = [2, "hour"];
            } else if (minutes <= 49 * 60) {
                this.plotOptions.xaxis.tickSize = [6, "hour"];
            } else if (minutes <= 97 * 60) {
                this.plotOptions.xaxis.tickSize = [12, "hour"];
            } else {
                this.plotOptions.xaxis.tickSize = [24, "hour"];
            }
        }

        private zoom(action: 'in' | 'out' | 'reset', centerTimestamp?: number): void {
            const startUnOffseted: number = this.applyOffset(this.currentTimeWindow.start, true);
            const endUnOffseted: number = this.applyOffset(this.currentTimeWindow.end, true);

            if (action === 'in' && (endUnOffseted - startUnOffseted) <= HistoricalTrendingController.ZOOM_MIN_RANGE) {
                return;
            }

            let zoom: number = (endUnOffseted - startUnOffseted) * HistoricalTrendingController.ZOOM_PERCENTAGE;
            zoom = Math.floor(zoom);
            if (zoom < HistoricalTrendingController.ZOOM_MIN_STEP) {
                zoom = HistoricalTrendingController.ZOOM_MIN_STEP;
            }

            let newInterval: {start: number, end: number};
            if (action === 'in') {
                if (centerTimestamp > startUnOffseted && centerTimestamp < endUnOffseted) {
                    newInterval = {
                        start: centerTimestamp - HistoricalTrendingController.ZOOM_MIN_RANGE / 2,
                        end: centerTimestamp + HistoricalTrendingController.ZOOM_MIN_RANGE / 2,
                    };
                } else {
                    newInterval = {
                        start: startUnOffseted + zoom,
                        end: endUnOffseted - zoom,
                    };
                }
            } else if (action === 'out') {
                newInterval = {
                    start: startUnOffseted - zoom,
                    end: endUnOffseted + zoom,
                };
            } else if (action === 'reset') {
                this.loadTrendingData();
                return;
            }

            if (newInterval.start < this.plotOptions.xaxis.panRange[0]) {
                newInterval.start = this.plotOptions.xaxis.panRange[0];
            }

            if (newInterval.end > this.plotOptions.xaxis.panRange[1]) {
                newInterval.end = this.plotOptions.xaxis.panRange[1];
            }

            if (newInterval !== undefined) {
                this.currentInterval = newInterval;
                this.plotOptions.xaxis.min = this.applyOffset(newInterval.start, false);
                this.plotOptions.xaxis.max = this.applyOffset(newInterval.end, false);
                this.loadTrendingData(newInterval);
            }
        }

        private bitsToMpbs(bits: number): number {
            if (bits === null)
                return null;
            if (bits !== undefined) {
                return parseFloat((bits / 1000000).toFixed(4));
            }
        }

        private applyOffset(date: number, substract: boolean = false): number {
            let offsetMs: number = this.timezoneService.getEffectiveOffsetSeconds() * 1000;
            return substract ? date - offsetMs : date + offsetMs;
        }
    }
}
